import { defineHttpContract } from "@/shared/api/contracts";
import {
    Message,
    ChatFile,
    ChatFileDownload,
    ChatFileUploadGrant,
    PagedResponse,
    Topic,
    Workspace,
} from "@/entities/chat/model/types";

export const crmContracts = {
    // Workspace
    getWorkspaces: defineHttpContract<Workspace[]>({
        service: "crm",
        method: "GET",
        path: "/workspaces",
    }),

    getWorkspace: (id: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "GET",
            path: `/workspaces/${id}`,
        }),

    updateWorkspaceName: (id: string) =>
        defineHttpContract<Workspace, { name: string }>({
            service: "crm",
            method: "PATCH",
            path: `/workspaces/${id}/name`,
        }),

    addWorkspaceMember: (id: string) =>
        defineHttpContract<Workspace, { memberId: string }>({
            service: "crm",
            method: "POST",
            path: `/workspaces/${id}/members`,
        }),

    removeWorkspaceMember: (id: string, memberId: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "DELETE",
            path: `/workspaces/${id}/members/${memberId}`,
        }),

    promoteWorkspaceMember: (id: string, memberId: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "PATCH",
            path: `/workspaces/${id}/members/${memberId}/promote`,
        }),

    demoteWorkspaceMember: (id: string, memberId: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "PATCH",
            path: `/workspaces/${id}/members/${memberId}/demote`,
        }),

    archiveWorkspace: (id: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "PATCH",
            path: `/workspaces/${id}/archive`,
        }),

    reactivateWorkspace: (id: string) =>
        defineHttpContract<Workspace>({
            service: "crm",
            method: "PATCH",
            path: `/workspaces/${id}/reactivate`,
        }),

    deleteWorkspace: (id: string) =>
        defineHttpContract<void>({
            service: "crm",
            method: "DELETE",
            path: `/workspaces/${id}`,
        }),

    // Topic
    getTopic: (id: string) =>
        defineHttpContract<Topic>({
            service: "crm",
            method: "GET",
            path: `/topics/${id}`,
        }),

    markTopicRead: (topicId: string) =>
        defineHttpContract<void>({
            service: "crm",
            method: "POST",
            path: `/topics/${topicId}/read`,
        }),

    getUserTopics: defineHttpContract<Topic[], undefined, { workspaceId?: string }>({
        service: "crm",
        method: "GET",
        path: `/topics`,
    }),

    getUserPinnedTopics: defineHttpContract<Topic[]>({
        service: "crm",
        method: "GET",
        path: `/topics/pinned`,
    }),

    getAllTopics: defineHttpContract<Topic[]>({
        service: "crm",
        method: "GET",
        path: "/topics",
    }),

    // Message
    getMessages: (topicId: string) =>
        defineHttpContract<PagedResponse<Message>, undefined, { page?: number; size?: number }>({
            service: "crm",
            method: "GET",
            path: `/topics/${topicId}/messages`,
        }),

    getPinnedMessages: (topicId: string) =>
        defineHttpContract<Message[]>({
            service: "crm",
            method: "GET",
            path: `/topics/${topicId}/messages/pinned`,
        }),

    getMessagesByStatus: (topicId: string, status: string) =>
        defineHttpContract<Message[]>({
            service: "crm",
            method: "GET",
            path: `/topics/${topicId}/messages/status/${status}`,
        }),

    // Files
    requestFileUpload: (topicId: string) =>
        defineHttpContract<ChatFileUploadGrant, { name: string; contentType: string; size: number }>({
            service: "crm",
            method: "POST",
            path: `/topics/${topicId}/files`,
        }),

    completeFileUpload: (topicId: string, fileId: string) =>
        defineHttpContract<ChatFile>({
            service: "crm",
            method: "POST",
            path: `/topics/${topicId}/files/${fileId}/complete`,
        }),

    getTopicFiles: (topicId: string) =>
        defineHttpContract<PagedResponse<ChatFile>, undefined, { page?: number; size?: number }>({
            service: "crm",
            method: "GET",
            path: `/topics/${topicId}/files`,
        }),

    getFileDownload: (topicId: string, fileId: string) =>
        defineHttpContract<ChatFileDownload>({
            service: "crm",
            method: "GET",
            path: `/topics/${topicId}/files/${fileId}`,
        }),
};
